import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip, IconButton, Typography, Box } from "@mui/material";

import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import BlockIcon from "@mui/icons-material/Block";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import DateRangeFilter from "../components/common/DateRangeFilter";
import StandardTable from "../components/common/StandardTable";
import StatusChip from "../components/common/StatusChip";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";

import CreateLicenseModal from "../components/licenses/CreateLicenseModal";
import UpdateLicenseModal from "../components/licenses/UpdateLicenseModal";
import UpgradeLicenseModal from "../components/licenses/UpgradeLicenseModal";
import ViewLicenseModal from "../components/licenses/ViewLicenseModal";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import {
  getLicenses,
  revokeLicense,
  resetLicenseServerStatus,
} from "../redux/actions/licenseActions";

const LicensesPage = () => {
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();

  const thirtyDaysAgo = new Date(new Date().setDate(new Date().getDate() - 30))
    .toISOString()
    .split("T")[0];
  const today = new Date().toISOString().split("T")[0];

  const {
    licenses,
    pagination,
    loadingLicenses,
    errorLoadingLicenses,
    serverStatus,
    loadingRevoke,
    currentLicense,
    loadingCreate,
    loadingUpdate,
    loadingUpgrade,
    errorCreating,
    errorUpdating,
    errorUpgrading,
  } = useSelector((state) => state.license);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [startDate, setStartDate] = useState(thirtyDaysAgo);
  const [endDate, setEndDate] = useState(today);
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });

  // Modal States
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [isUpgradeOpen, setIsUpgradeOpen] = useState(false);
  const [isRevokeOpen, setIsRevokeOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);

  const canRead = hasPermission("license:read_all");
  const canCreate = hasPermission("license:create");
  const canUpdate = hasPermission("license:update");
  const canRevoke = hasPermission("license:revoke");

  const fetchLicensesList = useCallback(() => {
    if (!canRead) return;
    dispatch(
      getLicenses({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch,
        startDate,
        endDate,
      }),
    );
  }, [
    dispatch,
    pageConfig.page,
    pageConfig.limit,
    debouncedSearch,
    startDate,
    endDate,
    canRead,
  ]);

  useEffect(() => {
    fetchLicensesList();
  }, [fetchLicensesList]);
  useEffect(() => {
    setPageConfig((p) => ({ ...p, page: 1 }));
  }, [debouncedSearch, startDate, endDate]);

  // --- Modal Handlers ---
  const handleOpenCreate = () => setIsCreateOpen(true);
  const handleCloseCreate = useCallback(() => {
    if (!loadingCreate) setIsCreateOpen(false);
  }, [loadingCreate]);

  const handleOpenUpdate = (license) => {
    setSelectedLicense(license);
    setIsUpdateOpen(true);
  };
  const handleCloseUpdate = useCallback(() => {
    if (!loadingUpdate) {
      setIsUpdateOpen(false);
      setSelectedLicense(null);
    }
  }, [loadingUpdate]);

  const handleOpenUpgrade = (license) => {
    setSelectedLicense(license);
    setIsUpgradeOpen(true);
  };
  const handleCloseUpgrade = useCallback(() => {
    if (!loadingUpgrade) {
      setIsUpgradeOpen(false);
      setSelectedLicense(null);
    }
  }, [loadingUpgrade]);

  const handleOpenRevoke = (license) => {
    setSelectedLicense(license);
    setIsRevokeOpen(true);
  };
  const handleCloseRevoke = useCallback(() => {
    if (!loadingRevoke) {
      setIsRevokeOpen(false);
      setSelectedLicense(null);
    }
  }, [loadingRevoke]);

  const handleOpenView = (license) => {
    setSelectedLicense(license);
    setIsViewOpen(true);
  };
  const handleCloseView = () => {
    setIsViewOpen(false);
    setSelectedLicense(null);
  };

  // UX Magic: Handle Server Successes
  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;

    if (
      (operation === "create" ||
        operation === "update" ||
        operation === "upgrade") &&
      (status === 200 || status === 201)
    ) {
      handleCloseCreate();
      handleCloseUpdate();
      handleCloseUpgrade();

      // The currentLicense was set in the action just before the success was dispatched.
      // We open the View modal so the user can immediately copy the Key.
      setIsViewOpen(true);

      dispatch(resetLicenseServerStatus());
    }

    if (operation === "revoke" && status === 200) {
      handleCloseRevoke();
      dispatch(resetLicenseServerStatus());
    }
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseUpgrade,
    handleCloseRevoke,
  ]);

  const columns = useMemo(
    () => [
      {
        id: "licenseKey",
        label: "License Key",
        minWidth: 140,
        format: (val) => (
          <Typography
            variant="caption"
            sx={{ fontFamily: "monospace", fontWeight: "bold" }}
          >
            {val.substring(0, 15)}...
          </Typography>
        ),
      },
      { id: "customerBusinessName", label: "Customer", minWidth: 150 },
      { id: "productName", label: "Product", minWidth: 120 },
      { id: "type", label: "Type", minWidth: 100 },
      {
        id: "expiryDate",
        label: "Expiry Date",
        minWidth: 110,
        format: (val) => new Date(val).toLocaleDateString(),
      },
      {
        id: "status",
        label: "Status",
        minWidth: 110,
        format: (val) => <StatusChip status={val} />,
      },
    ],
    [],
  );

  const renderRowActions = (row) => (
    <Box display="flex" justifyContent="center">
      <Tooltip title="View License">
        <IconButton
          size="small"
          color="primary"
          onClick={() => {
            setSelectedLicense(row);
            setIsViewOpen(true);
          }}
        >
          <VisibilityOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {canUpdate && (
        <Tooltip title="Edit License">
          <IconButton
            size="small"
            color="secondary"
            onClick={() => {
              setSelectedLicense(row);
              setIsUpdateOpen(true);
            }}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {canUpdate && (
        <Tooltip title="Upgrade License">
          <IconButton
            size="small"
            color="info"
            onClick={() => {
              setSelectedLicense(row);
              setIsUpgradeOpen(true);
            }}
          >
            <TrendingUpIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {canRevoke && row.status !== "REVOKED" && (
        <Tooltip title="Revoke">
          <IconButton
            size="small"
            color="error"
            onClick={() => {
              setSelectedLicense(row);
              setIsRevokeOpen(true);
            }}
          >
            <BlockIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  return (
    <PageContainer
      title="License Management"
      subtitle="Generate, monitor, and manage software licenses and expiry dates."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setIsCreateOpen(true)}
          >
            Generate License
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search Key or Customer..."
      >
        <DateRangeFilter
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
      </PageToolbar>

      <StandardTable
        columns={columns}
        data={licenses}
        loading={loadingLicenses}
        error={errorLoadingLicenses}
        onRetry={fetchLicensesList}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(p) => setPageConfig((c) => ({ ...c, page: p }))}
        onRowsPerPageChange={(l) =>
          setPageConfig((c) => ({ ...c, limit: l, page: 1 }))
        }
        rowAction={renderRowActions}
      />

      {/* Modals */}
      {isCreateOpen && (
        <CreateLicenseModal
          open={handleOpenCreate}
          onClose={handleCloseCreate}
          loading={loadingCreate}
          error={errorCreating}
        />
      )}

      {isUpdateOpen && selectedLicense && (
        <UpdateLicenseModal
          open={handleOpenUpdate}
          onClose={handleCloseUpdate}
          license={currentLicense || selectedLicense}
          loading={loadingUpdate}
          error={errorUpdating}
        />
      )}

      {isUpgradeOpen && selectedLicense && (
        <UpgradeLicenseModal
          open={handleOpenUpgrade}
          onClose={handleCloseUpgrade}
          license={currentLicense || selectedLicense}
          loading={loadingUpgrade}
          error={errorUpgrading}
        />
      )}

      {/* Uses currentLicense from Redux if it just got generated/updated, otherwise the one clicked from the table */}
      {isViewOpen && (selectedLicense || currentLicense) && (
        <ViewLicenseModal
          open={handleOpenView}
          onClose={handleCloseView}
          license={serverStatus ? currentLicense : selectedLicense}
        />
      )}

      {isRevokeOpen && selectedLicense && (
        <ConfirmDialog
          open={handleOpenRevoke}
          onClose={handleCloseRevoke}
          onConfirm={() => dispatch(revokeLicense(selectedLicense.id))}
          title="Revoke License"
          color="error"
          confirmText="Revoke"
          loading={loadingRevoke}
          message={`Are you sure you want to completely revoke license key ending in ${selectedLicense.licenseKey.slice(-6)}? This will immediately stop the software from working.`}
        />
      )}
    </PageContainer>
  );
};
export default LicensesPage;
